import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { debugLog } from '../../utils/debugLog';

const ProtectedRoute = ({ children }) => {
  const theme = useTheme();
  const { isAuthenticated, loading, getTenantAlias } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    debugLog('🔒 ProtectedRoute: User not authenticated, redirecting...');

    // Get tenant from AuthContext (handles subdomain, query param, storage with validation)
    let tenant = getTenantAlias();

    // If no tenant from context, try URL query param as fallback
    if (!tenant) {
      const urlParams = new URLSearchParams(window.location.search);
      tenant = urlParams.get('tenant');
    }

    // If no tenant found, redirect to login (login page will handle tenant selection if needed)
    if (!tenant) {
      debugLog('🔒 No tenant found, redirecting to login');
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Construct login URL with tenant parameter
    const loginPath = `/login?tenant=${encodeURIComponent(tenant)}`;
    debugLog('🔒 Redirecting to login with tenant:', loginPath);

    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Render children if authenticated
  debugLog('✅ ProtectedRoute: User authenticated, rendering children');
  return children;
};

export default ProtectedRoute;
